import { Fragment, useContext, useEffect, useState } from "react";
import MuiAlert from "../../components/Alert/Alert";
import ApiService from "../../services/ApiService";
import { IRate } from "../../helpers/Interfaces";
import Input from "../../components/Input/Input";
import DocumentButton from "../../components/DocumentButton/DocumentButton";
import { validNumber } from "../../helpers/HelperFunctions";

import "./Rate.scss";

function RateSettings() {

    const apiService = new ApiService();

    const [formData, setFormData] = useState<{ tier1: IRate[], tier2: IRate[], tier3: IRate[], tier4: IRate[] }>({
        tier1: [], tier2: [], tier3: [], tier4: []
    });
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });

    useEffect(() => {
        getRates();
    }, [])

    function getRates() {

        setLoading(true);

        apiService.getRates().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            setFormData({ tier1: data?.tier1, tier2: data?.tier2, tier3: data?.tier3, tier4: data?.tier4 });
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getRateValue(tierData: IRate[], stage: string, urgency: string) {
        let rate = tierData.filter(item => item.rateName === stage && item.urgency === urgency);

        if (rate.length > 0) {
            return rate[0].rate.toString();
        }

        return "0";
    }

    function setRateValue(tierKey: keyof typeof formData, stage: string, urgency: string, newRate: any) {
        if (validNumber(newRate)) {

            const tierData = formData[tierKey];

            const itemIndex = tierData.findIndex(
                item => item.rateName === stage && item.urgency === urgency
            );

            if (itemIndex > -1) {
                const newTierData = [...tierData];

                newTierData[itemIndex] = {
                    ...newTierData[itemIndex],
                    rate: newRate
                };

                setFormData(prevFormData => ({
                    ...prevFormData,
                    [tierKey]: newTierData,
                }));
            }
        }
    }

    function renderTier(tier: keyof typeof formData, tierData: IRate[]) {
        return (<div className="table">
            <div className="row">
                <span className="header">Stage</span>
                <span className="header">Normal</span>
                <span className="header">Urgent</span>
                <span className="header">Super Urgent</span>
            </div>
            <div className="row">
                <span className="header">Scribing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing", "Super Urgent", value) }} />
                </div>
            </div>
            <div className="row">
                <span className="header">Proofing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Proofing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Proofing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Proofing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Proofing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Proofing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Proofing", "Super Urgent", value) }} />
                </div>
            </div>
            <div className="row">
                <span className="header">Scribing + Proofing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing + Proofing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing + Proofing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing + Proofing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing + Proofing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Scribing + Proofing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Scribing + Proofing", "Super Urgent", value) }} />
                </div>
            </div>
            <div className="row">
                <span className="header">Line Scribing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing", "Super Urgent", value) }} />
                </div>
            </div>
            <div className="row">
                <span className="header">Line Proofing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Proofing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Proofing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Proofing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Proofing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Proofing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Proofing", "Super Urgent", value) }} />
                </div>
            </div>
            <div className="row">
                <span className="header">Line Scribing + Proofing</span>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing + Proofing", "Normal")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing + Proofing", "Normal", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing + Proofing", "Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing + Proofing", "Urgent", value) }} />
                </div>
                <div className="input">
                    <Input label='' value={getRateValue(tierData, "Line Scribing + Proofing", "Super Urgent")} type='text'
                        onChange={(value: any) => { setRateValue(tier, "Line Scribing + Proofing", "Super Urgent", value) }} />
                </div>
            </div>
        </div>)
    }

    function update() {

        setLoading(true);

        apiService.updateRates(formData).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Updated";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));

            getRates();
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }
    return (
        <div className='rates-container'>
            <MuiAlert error={showAlert.isError} message={showAlert.message} open={showAlert.open}
                onClose={() => {
                    let newShowAlert = showAlert;
                    newShowAlert.open = false;
                    setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert, }));
                }} />
            <div className="header">
                <span className="title">Rate Settings</span>
                <span className="header-text">Update company-wide rates for doctors</span>
            </div>
            <div className="body">
                {formData.tier1.length > 0
                    ? (<div className="tier-wrapper">
                        <div className="tier">TIER 1</div>
                        {renderTier("tier1", formData.tier1)}
                    </div>)
                    : (<Fragment />)
                }
                {formData.tier2.length > 0
                    ? (<div className="tier-wrapper">
                        <div className="tier">TIER 2</div>
                        {renderTier("tier2", formData.tier2)}
                    </div>)
                    : (<Fragment />)
                }
                {formData.tier3.length > 0
                    ? (<div className="tier-wrapper">
                        <div className="tier">TIER 3</div>
                        {renderTier("tier3", formData.tier3)}
                    </div>)
                    : (<Fragment />)
                }
                {formData.tier4.length > 0
                    ? (<div className="tier-wrapper">
                        <div className="tier">TIER 4</div>
                        {renderTier("tier4", formData.tier4)}
                    </div>)
                    : (<Fragment />)
                }
            </div>
            <div className="footer">
                <div className="button">
                    <DocumentButton loading={loading} onClick={() => { update(); }} text={"Update"} />
                </div>
            </div>
        </div >
    )
}

export default RateSettings